import { Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { CompanyEditScope } from 'mormaii-web-presentation/company-edit'

import { Modal } from '../../../../components/dialog'
import { Button, ChoiceFilter, TextFilter } from '../../../../components/filters'

import Css from './company-edit.module.scss'

/**
 * O formulario de empresa - cadastrar e editar sao a MESMA tela.
 *
 * O que muda entre os dois e o titulo e se o CNPJ aceita edicao, e as duas coisas
 * vem do scope (`title`, `creating`). Duplicar a tela para mudar isso seria manter
 * sete campos em dois lugares.
 */
export function CompanyEditView(props: ViewProps<CompanyEditScope>): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSave()
    }

    return (
        <Modal
            wide
            title={props.scope.title}
            onClose={() => void props.scope.onClose()}
            footer={
                <>
                    <Button variant="ghost" onClick={() => void props.scope.onClose()}>
                        Cancelar
                    </Button>
                    <Button form="company-edit" type="submit" disabled={props.scope.busy || props.scope.loading}>
                        {props.scope.busy ? 'Salvando…' : props.scope.loading ? 'Carregando…' : 'Salvar'}
                    </Button>
                </>
            }
        >
            {/*
                O botao de salvar fica no RODAPE do dialogo, fora do `<form>`, e se
                liga a ele pelo atributo `form`. E o que permite o rodape ficar preso
                enquanto o corpo rola, sem tirar do botao o comportamento de submeter
                (Enter num campo continua salvando).
            */}
            <form id="company-edit" onSubmit={submit} class={Css.form}>
                <Field
                    label="CNPJ"
                    value={props.scope.cnpj}
                    error={props.scope.cnpjError}
                    onChange={(value) => props.scope.onCnpjChanged(value)}
                />
                <Field
                    label="Razão social"
                    value={props.scope.razaoSocial}
                    error={props.scope.razaoSocialError}
                    onChange={(value) => props.scope.onRazaoSocialChanged(value)}
                />
                <Field
                    label="Nome fantasia"
                    value={props.scope.nomeFantasia}
                    onChange={(value) => props.scope.onNomeFantasiaChanged(value)}
                />
                <Field
                    label="Ramo de atividade"
                    value={props.scope.ramoAtividade}
                    onChange={(value) => props.scope.onRamoAtividadeChanged(value)}
                />

                {/*
                    A classificacao que o ICP usa - so para o administrador geral, o unico
                    papel cujos dois campos o servidor grava.
                */}
                <Show when={props.scope.icpEditable}>
                    <ChoiceFilter
                        label="Segmento"
                        summary={props.scope.segmentSummary}
                        placeholder="Sem segmento"
                        hasValue={!!props.scope.segmentSummary}
                        clearable={props.scope.segmentClearable}
                        onClear={() => void props.scope.onClearSegment()}
                        options={props.scope.segments.map((segment) => ({
                            label: segment.label,
                            value: segment.value,
                            current: segment.current,
                            onSelect: segment.onSelect
                        }))}
                    />
                    <Field
                        label="Colaboradores contratados"
                        value={props.scope.contractedHeadcount}
                        error={props.scope.contractedHeadcountError}
                        onChange={(value) => props.scope.onContractedHeadcountChanged(value)}
                    />
                </Show>

                <p class={Css.section}>Contato</p>

                <Field
                    label="Nome"
                    value={props.scope.contactName}
                    error={props.scope.contactNameError}
                    onChange={(value) => props.scope.onContactNameChanged(value)}
                />
                {/*
                    O CPF so faz sentido no CADASTRO: ele e a identidade do
                    administrador que nasce junto com a empresa. Em branco, o servidor
                    ainda fabrica um a partir do CNPJ - e a tela de usuarios da
                    empresa marca esse para quem quiser corrigir depois.
                */}
                <Show when={props.scope.creating}>
                    <Field
                        label="CPF do contato"
                        value={props.scope.contactCpf}
                        error={props.scope.contactCpfError}
                        onChange={(value) => props.scope.onContactCpfChanged(value)}
                    />
                </Show>

                <Field
                    label="Telefone"
                    value={props.scope.contactPhone}
                    error={props.scope.contactPhoneError}
                    onChange={(value) => props.scope.onContactPhoneChanged(value)}
                />
                <Field
                    label="E-mail"
                    value={props.scope.contactEmail}
                    error={props.scope.contactEmailError}
                    onChange={(value) => props.scope.onContactEmailChanged(value)}
                    wide
                />
            </form>
        </Modal>
    )
}

/** Um campo do formulario, com o erro embaixo quando ha. */
function Field(props: {
    label: string
    value: string
    error?: string
    wide?: boolean
    onChange: (value: string) => void
}): JSX.Element {
    return (
        <div classList={{ [Css.wide]: props.wide }}>
            <TextFilter label={props.label} value={props.value} onChange={props.onChange} />
            {props.error ? <p class={Css.error}>{props.error}</p> : null}
        </div>
    )
}
